import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PasswordFields } from "@/components/PasswordFields";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [valid, setValid] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!valid) { setError("Please meet all password requirements and confirm they match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset. Please log in.");
      navigate("/login");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-20 text-center">
        <h1 className="font-display text-3xl font-semibold">Invalid reset link</h1>
        <p className="text-muted-foreground mt-2">This link is missing or malformed.</p>
        <Link to="/forgot-password"><Button className="rounded-sm mt-6">Request a new link</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-20">
      <div className="flex items-center gap-2 text-primary"><KeyRound className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-bold">Reset</span></div>
      <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">Set a new password</h1>
      <form onSubmit={submit} className="mt-8 space-y-5">
        <PasswordFields onChange={(pw, v) => { setPassword(pw); setValid(v); }} autoFocus />
        {error && <p className="text-destructive text-sm" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading || !valid} className="w-full rounded-sm" data-testid="reset-submit">{loading ? "Resetting…" : "Reset password"}</Button>
      </form>
    </div>
  );
}
